import { useMemo, useState, type ReactNode } from "react";
import { ConfirmDialog, ExtendedCard } from "@NP-Webber/crm-kit";
import {
  meetingsApi,
  type MeetingActionItem,
  type MeetingAgendaItem,
  type MeetingFull,
  type MeetingParticipant,
  type MeetingTopic,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import {
  AddOutlinedIcon,
  AssignmentOutlinedIcon,
  ClearOutlinedIcon,
  EditOutlinedIcon,
  GroupsOutlinedIcon,
  NotesOutlinedIcon,
  PrintOutlinedIcon,
  SendOutlinedIcon,
  TaskAltOutlinedIcon,
} from "../../../ui/appIcons";
import {
  ACTION_STATUS_LABELS,
  formatMeetingDateTime,
  MEETING_ROLE_LABELS,
  MEETING_STATUS_COLORS,
  MEETING_STATUS_LABELS,
  PARTICIPANT_KIND_LABELS,
  toDateInputValue,
} from "../constants";
import { printTeamMeeting } from "../teamMeetingPrint";

function EmptySection({ children }: { children: ReactNode }) {
  return <div className="tm-empty tm-empty--section">{children}</div>;
}

function ParticipantsTab({ participants }: { participants: MeetingParticipant[] }) {
  if (!participants.length) return <EmptySection>אין משתתפים</EmptySection>;
  return (
    <div className="tm-chips" style={{ padding: 8 }}>
      {participants.map((p) => (
        <span key={p.id || `${p.userId}-${p.guestName}`} className="tm-chip">
          {p.displayName || p.guestName}
          <span className="tm-chip__role">
            {MEETING_ROLE_LABELS[p.meetingRole] || p.meetingRole}
          </span>
          <span
            className={`tm-chip__kind${p.participantKind === "guest" ? " tm-chip__kind--guest" : ""}`}
          >
            {PARTICIPANT_KIND_LABELS[p.participantKind] || p.participantKind}
          </span>
          <span className={`tm-chip__attend${p.attended ? " tm-chip__attend--yes" : ""}`}>
            {p.attended ? "נכח" : "לא סומן"}
          </span>
        </span>
      ))}
    </div>
  );
}

function AgendaTab({ agendaItems }: { agendaItems: MeetingAgendaItem[] }) {
  if (!agendaItems.length) return <EmptySection>אין נושאים בסדר היום</EmptySection>;
  return (
    <div style={{ padding: 8 }}>
      {agendaItems.map((a) => (
        <div key={a.id} className="tm-agenda-item" style={{ marginBottom: 8 }}>
          <div className="tm-agenda-item__body">
            <div className="tm-agenda-item__title-row">
              <div className="tm-agenda-item__title">{a.title}</div>
              <span className={`tm-mini-badge${a.wasDiscussed ? " tm-mini-badge--ok" : ""}`}>
                {a.wasDiscussed ? "נידון" : "לא נידון"}
              </span>
            </div>
            {a.notes ? <div className="tm-agenda-item__notes">{a.notes}</div> : null}
            {a.submittedByName ? (
              <div className="tm-agenda-item__meta">הוגש ע״י {a.submittedByName}</div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function TopicsTab({
  topics,
  emptyMsg,
}: {
  topics: MeetingTopic[];
  emptyMsg: string;
}) {
  if (!topics.length) return <EmptySection>{emptyMsg}</EmptySection>;
  return (
    <div style={{ padding: 8 }}>
      {topics.map((t) => (
        <div key={t.id} className="tm-topic-card">
          <div className="tm-topic-card__head" style={{ cursor: "default" }}>
            {t.title}
          </div>
          <div className="tm-topic-card__body">
            <p>
              <strong>סיכום:</strong> {t.summary?.trim() ? t.summary : "—"}
            </p>
            <p>
              <strong>החלטות:</strong> {t.decisions?.trim() ? t.decisions : "—"}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionsTab({
  actions,
  topicById,
}: {
  actions: MeetingActionItem[];
  topicById: Record<string, MeetingTopic>;
}) {
  if (!actions.length) return <EmptySection>אין דרכי פעולה</EmptySection>;
  return (
    <div style={{ padding: 8 }}>
      {actions.map((a) => {
        const topicTitle = a.topicId ? topicById[a.topicId]?.title : null;
        return (
          <div key={a.id} className="tm-record-action">
            <div className="tm-record-action__head">
              <div className="tm-agenda-item__title">{a.description}</div>
              <span
                className={`tm-status-chip tm-status-chip--sm tm-status-chip--action-${a.status || "open"}`}
              >
                {ACTION_STATUS_LABELS[a.status || ""] || a.status || "פתוח"}
              </span>
            </div>
            {a.technicalDetails?.trim() ? (
              <div className="tm-record-action__details">{a.technicalDetails}</div>
            ) : null}
            <div className="tm-agenda-item__meta">
              {a.assignedToName ? `אחראי: ${a.assignedToName}` : ""}
              {a.dueDate
                ? `${a.assignedToName ? " · " : ""}יעד: ${toDateInputValue(a.dueDate)}`
                : ""}
              {topicTitle
                ? `${a.assignedToName || a.dueDate ? " · " : ""}נושא: ${topicTitle}`
                : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type Props = {
  open: boolean;
  meeting: MeetingFull | null;
  onClose: () => void;
  canManage: boolean;
  onRefresh?: () => void;
  onCreateFromPrevious?: (id: string) => void;
  onResumeMeeting?: (meeting: MeetingFull) => void;
};

export function TeamMeetingCard({
  open,
  meeting,
  onClose,
  canManage,
  onRefresh,
  onCreateFromPrevious,
  onResumeMeeting,
}: Props) {
  const [sending, setSending] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const topicById = useMemo(() => {
    const map: Record<string, MeetingTopic> = {};
    (meeting?.topics || []).forEach((t) => {
      map[t.id] = t;
    });
    return map;
  }, [meeting?.topics]);

  if (!open || !meeting) return null;

  const participants = meeting.participants || [];
  const agendaItems = meeting.agendaItems || [];
  const topics = meeting.topics || [];
  const actions = meeting.actionItems || [];
  const isOpenMeeting =
    meeting.status === "preparation" || meeting.status === "scheduled";
  const isCompleted = meeting.status === "completed";
  const statusColor = MEETING_STATUS_COLORS[meeting.status] || "#6B7280";
  const topicsEmptyMsg =
    isCompleted || meeting.status === "cancelled" ? "אין סיכומים" : "טרם נכתב סיכום";

  const detailsFields = [
    {
      key: "scheduledAt",
      label: "תאריך",
      value: formatMeetingDateTime(meeting.scheduledAt),
    },
    meeting.durationMinutes != null
      ? {
          key: "duration",
          label: "משך",
          value: `${meeting.durationMinutes} דק׳`,
        }
      : null,
    meeting.completedAt
      ? {
          key: "completedAt",
          label: "הושלמה",
          value: formatMeetingDateTime(meeting.completedAt),
        }
      : null,
    meeting.createdByName
      ? { key: "createdBy", label: "נוצר ע״י", value: meeting.createdByName }
      : null,
    meeting.summarySentAt
      ? {
          key: "summarySent",
          label: "סיכום נשלח",
          value: formatMeetingDateTime(meeting.summarySentAt),
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; value: string }>;

  const handleResendSummary = async () => {
    setSending(true);
    try {
      await meetingsApi.sendSummary(meeting.id);
      onRefresh?.();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    } finally {
      setSending(false);
    }
  };

  const handleCancelMeeting = async () => {
    setCancelBusy(true);
    try {
      await meetingsApi.update(meeting.id, { status: "cancelled" });
      setCancelOpen(false);
      onRefresh?.();
      onClose();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    } finally {
      setCancelBusy(false);
    }
  };

  const headerActions = [
    {
      tooltip: "הדפסה / PDF",
      icon: <PrintOutlinedIcon />,
      onClick: () => printTeamMeeting(meeting),
    },
  ];
  if (canManage && isCompleted) {
    headerActions.push({
      tooltip: sending ? "שולח…" : "שלח סיכום שוב",
      icon: <SendOutlinedIcon />,
      onClick: () => {
        void handleResendSummary();
      },
      disabled: sending,
    } as (typeof headerActions)[number] & { disabled?: boolean });
  }
  if (canManage && isOpenMeeting) {
    headerActions.push({
      tooltip: "המשך עריכה",
      icon: <EditOutlinedIcon />,
      onClick: () => onResumeMeeting?.(meeting),
    });
    headerActions.push({
      tooltip: "ביטול ישיבה",
      icon: <ClearOutlinedIcon />,
      onClick: () => setCancelOpen(true),
    });
  }
  if (canManage) {
    headerActions.push({
      tooltip: "פתח ישיבה חדשה (מבוסס על קודמת)",
      icon: <AddOutlinedIcon />,
      onClick: () => onCreateFromPrevious?.(meeting.id),
    });
  }

  const extraTabs = [
    {
      label: `משתתפים (${participants.length})`,
      icon: <GroupsOutlinedIcon />,
      content: <ParticipantsTab participants={participants} />,
    },
    {
      label: `סדר יום (${agendaItems.length})`,
      icon: <AssignmentOutlinedIcon />,
      content: <AgendaTab agendaItems={agendaItems} />,
    },
    {
      label: `סיכומים (${topics.length})`,
      icon: <NotesOutlinedIcon />,
      content: <TopicsTab topics={topics} emptyMsg={topicsEmptyMsg} />,
    },
    {
      label: `דרכי פעולה (${actions.length})`,
      icon: <TaskAltOutlinedIcon />,
      content: <ActionsTab actions={actions} topicById={topicById} />,
    },
  ];

  return (
    <>
    <ExtendedCard
      open={open}
      onClose={onClose}
      title={meeting.title || "ישיבת צוות"}
      subtitle={formatMeetingDateTime(meeting.scheduledAt)}
      statusBadge={{
        label: MEETING_STATUS_LABELS[meeting.status] || meeting.status,
        bg: statusColor,
        color: "#fff",
      }}
      detailsFields={detailsFields}
      headerActions={headerActions}
      hideFeedTab
      feedSources={{}}
      defaultTab={0}
      extraTabs={extraTabs}
    />
    <ConfirmDialog
      open={cancelOpen}
      onClose={() => {
        if (!cancelBusy) setCancelOpen(false);
      }}
      title="ביטול ישיבה"
      message={`לבטל את הישיבה "${meeting.title || "ישיבת צוות"}"?`}
      confirmText="ביטול ישיבה"
      cancelText="חזרה"
      loading={cancelBusy}
      onConfirm={() => {
        void handleCancelMeeting();
      }}
      onCancel={() => setCancelOpen(false)}
    />
    </>
  );
}
